import type { RasterLayer } from "../document/Layer";
import { findRaster } from "../document/LayerUtils";
import { screenToDoc } from "../render/Viewport";
import type { PointerSample, RGB, Tool, ToolContext } from "./Tool";

export type SampleSize = "point" | "3x3" | "5x5" | "11x11" | "31x31" | "51x51" | "101x101";

/**
 * Optional composite-canvas reader injected by the host so EyedropperTool
 * can read pixels from the rendered composite without depending on DOM.
 * Returns the averaged colour over an NxN window or null if out of bounds.
 */
export type CompositeReader = (
  docPt: { x: number; y: number },
  n: number,
) => RGB | null;

export class EyedropperTool implements Tool {
  readonly id = "eyedropper";
  readonly shortcut = "I";

  /** Size of the sample area: single point or NxN average. */
  sampleSize: SampleSize = "point";

  /** When true, samples from all visible layers composited together. */
  sampleAllLayers = false;

  /** When true, shows a sampling ring cursor around the eyedropper. */
  showSamplingRing = true;

  /**
   * Optional composite reader supplied by main.ts (the host). Lets the tool
   * read NxN pixels from the rendered composite when `sampleAllLayers` is on
   * without dragging DOM/document references into src/core.
   */
  compositeReader: CompositeReader | null = null;

  /**
   * The color the eyedropper would commit if pointer-up fired now. The cursor
   * overlay reads this to draw the sampling-ring's top half. null between strokes.
   */
  currentSample: RGB | null = null;

  /**
   * Foreground (or background, for Alt-drag) color at drag start. Drawn in
   * the sampling ring's bottom half so the user can compare what they had
   * vs. what they are about to pick. null between strokes.
   */
  previousColor: RGB | null = null;

  /** True while the pointer is down on the canvas. */
  private dragActive = false;

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    this.previousColor = s.altKey ? ctx.bgColor : ctx.fgColor;
    this.dragActive = true;
    this.sample(ctx, s);
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.dragActive) {
      // Hover preview only — compute sample but do not commit.
      this.currentSample = this.computeSample(ctx, s);
      return;
    }
    this.sample(ctx, s);
  }

  onPointerUp(_ctx: ToolContext, _s: PointerSample): void {
    this.dragActive = false;
    this.currentSample = null;
    this.previousColor = null;
  }

  /** Compute the sample color without committing — used for hover preview. */
  private computeSample(ctx: ToolContext, s: PointerSample): RGB | null {
    if (!ctx.doc) return null;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    if (this.sampleAllLayers) {
      if (this.compositeReader) return this.compositeReader(p, this.sampleRadius());
      // No composite reader injected — fall back to the active layer only.
    }
    const layerId = ctx.doc.activeLayerId;
    if (!layerId) return null;
    const layer = findRaster(ctx.doc.layers, layerId);
    if (!layer || !layer.visible) return null;
    return this.sampleFromLayer(layer, p);
  }

  private sample(ctx: ToolContext, s: PointerSample): void {
    const color = this.computeSample(ctx, s);
    if (!color) return;
    this.currentSample = color;
    if (s.altKey) ctx.setBackgroundColor(color);
    else ctx.setForegroundColor(color);
  }

  sampleFromLayer(
    layer: RasterLayer,
    docPt: { x: number; y: number },
  ): RGB | null {
    const px = Math.floor(docPt.x - layer.x);
    const py = Math.floor(docPt.y - layer.y);
    const n = this.sampleRadius();
    if (n === 0) {
      // Single point
      if (px < 0 || py < 0 || px >= layer.pixels.width || py >= layer.pixels.height) return null;
      const i = (py * layer.pixels.width + px) * 4;
      const a = layer.pixels.data[i + 3] ?? 0;
      if (a === 0) return null;
      return {
        r: layer.pixels.data[i] ?? 0,
        g: layer.pixels.data[i + 1] ?? 0,
        b: layer.pixels.data[i + 2] ?? 0,
      };
    }
    // NxN average
    let rSum = 0,
      gSum = 0,
      bSum = 0,
      count = 0;
    for (let dy = -n; dy <= n; dy++) {
      for (let dx = -n; dx <= n; dx++) {
        const sx = px + dx;
        const sy = py + dy;
        if (sx < 0 || sy < 0 || sx >= layer.pixels.width || sy >= layer.pixels.height) continue;
        const i = (sy * layer.pixels.width + sx) * 4;
        const a = layer.pixels.data[i + 3] ?? 0;
        if (a === 0) continue;
        rSum += layer.pixels.data[i] ?? 0;
        gSum += layer.pixels.data[i + 1] ?? 0;
        bSum += layer.pixels.data[i + 2] ?? 0;
        count++;
      }
    }
    if (count === 0) return null;
    return {
      r: Math.round(rSum / count),
      g: Math.round(gSum / count),
      b: Math.round(bSum / count),
    };
  }

  /**
   * Half-size of the NxN window. point -> 0, 3x3 -> 1, 5x5 -> 2 ... 101x101 -> 50.
   * Public so the cursor renderer can draw the sampled area ring at the
   * correct size when Show Sampling Ring is on.
   */
  sampleRadius(): number {
    switch (this.sampleSize) {
      case "3x3":
        return 1;
      case "5x5":
        return 2;
      case "11x11":
        return 5;
      case "31x31":
        return 15;
      case "51x51":
        return 25;
      case "101x101":
        return 50;
      default:
        return 0;
    }
  }
}
